import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  BookOpen, 
  AlertTriangle, 
  Download, 
  MessageSquare, 
  ChevronDown,
  Info,
  Bot,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { sendWhatsAppNotification, getWhatsAppMessageText, enrichAppWithProfessionalData } from '../utils/whatsapp';
import './WhatsAppManual.css';

const WhatsAppManual = () => {
  const { addNotification } = useNotifications();
  const [professionals, setProfessionals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';

  // Default dates to tomorrow (standard for upcoming reminders)
  const getTomorrowStr = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const tomorrowStr = getTomorrowStr();

  const [startDate, setStartDate] = useState(tomorrowStr);
  const [endDate, setEndDate] = useState(tomorrowStr);
  const [selectedProf, setSelectedProf] = useState(isAdmin ? 'TODOS' : (staffName || 'TODOS'));
  const [sendType, setSendType] = useState('web');
  const [expandedRows, setExpandedRows] = useState({});

  async function fetchConfig() {
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('*')
        .order('full_name', { ascending: true });
      if (error) throw error;
      setProfessionals(data || []);
    } catch (err) {
      console.error("Error fetching professionals for WhatsApp manual:", err);
    }
  }

  async function fetchAppointments() {
    setLoading(true);
    try {
      let query = supabase
        .from('appointments')
        .select('*')
        .gte('appointment_date', startDate)
        .lte('appointment_date', endDate);

      if (selectedProf && selectedProf !== 'TODOS') {
        query = query.eq('professional_name', selectedProf);
      }

      const { data, error } = await query
        .order('appointment_date', { ascending: true })
        .order('appointment_time', { ascending: true });
      
      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error("Error fetching appointments for WhatsApp manual:", err);
      addNotification('Error al cargar turnos de la base de datos.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchConfig();
      fetchAppointments();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async (app) => {
      const template = localStorage.getItem('gdt_wa_auto_template') || 
        'Hola {paciente}, te recordamos tu turno para el día {fecha} a las {hora} hs. Por favor, confirma tu asistencia ingresando aquí: {link_confirmacion}';
      
      // Enrich with professional's branch & custom template
      const enrichedApp = await enrichAppWithProfessionalData(app);
      const success = await sendWhatsAppNotification(enrichedApp, template, addNotification);
      if (success) {
        fetchAppointments();
      }
    };

  const isPastAppointment = (date, time) => {
    if (!date) return false;
    const now = new Date();
    const appDateTime = new Date(`${date}T${time || '00:00:00'}`);
    return appDateTime < now;
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleExport = () => {
    if (appointments.length === 0) {
      addNotification('No hay datos para exportar.', 'warning');
      return;
    }

    const headers = ['Fecha', 'Staff', 'Hora', 'Paciente', 'Telefono', 'Pasado', 'Preparado'];
    const rows = appointments.map(rem => [
      rem.appointment_date,
      rem.professional_name || 'N/A',
      rem.appointment_time?.substring(0, 5) || '',
      rem.patient_name || '',
      rem.patient_phone || '',
      isPastAppointment(rem.appointment_date, rem.appointment_time) ? 'SI' : 'NO',
      rem.patient_phone ? 'SI' : 'NO'
    ]);

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `recordatorios_manuales_${startDate}_a_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addNotification('Listado exportado a CSV con éxito.', 'success');
  };

  const handleConfigClick = () => {
    window.location.hash = '#/reminders';
    addNotification('Abriendo configuración de plantilla y API de WhatsApp...', 'info');
  };

  return (
    <div className="whatsapp-reminders-page">
      <div className="page-header-rem">
        <h1>Armado de Mensajes para envío de Recordatorios por WhatsApp</h1>
        <div className="header-btns">
          <button className="btn-config" onClick={handleConfigClick}><Settings size={16} /> CONFIGURAR</button>
          <button className="btn-guide" onClick={() => addNotification('Manual: Use los filtros para ver turnos y envíe mensajes individuales usando las plantillas configuradas.', 'info')}><BookOpen size={16} /> VER INSTRUCTIVO DE USO</button>
        </div>
      </div>

      {showAlert && (
        <div className="alert-notice">
          <div className="alert-icon"><AlertTriangle size={24} /></div>
          <div className="alert-content">
            <h3>Importante ¡LEER antes de comenzar!</h3>
            <p>Para evitar bloqueos o restricciones de WhatsApp, recomendamos enviar los recordatorios de a poco, especialmente si es la primera vez que usa este módulo o si hay muchos turnos en un mismo día. WhatsApp puede interpretar múltiples envíos seguidos como spam y suspender la cuenta, y BlipDoc no asume responsabilidad por dichas suspensiones.</p>
            <button className="btn-info-sm" onClick={() => addNotification('Consulte las políticas de spam de WhatsApp para más detalles.', 'info')}>+ INFORMACIÓN</button>
          </div>
          <button type="button" className="btn-close-alert" onClick={() => setShowAlert(false)}>×</button>
        </div>
      )}

      <div className="filters-card card">
        <div className="filter-row">
          <div className="filter-item">
            <label>Fecha Desde</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
            />
          </div>
          <div className="filter-item">
            <label>Fecha Hasta</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)} 
            />
          </div>
          <div className="filter-item">
            <label>Staff Profesional</label>
            <select 
              value={selectedProf} 
              onChange={(e) => setSelectedProf(e.target.value)}
              disabled={!isAdmin}
            >
              {!isAdmin ? (
                 <option value={staffName}>{staffName}</option>
              ) : (
                <>
                  <option value="TODOS">TODOS</option>
                  {professionals.map(p => {
                    const name = p.full_name || p.name;
                    return <option key={p.id} value={name}>{name}</option>;
                  })}
                </>
              )}
            </select>
          </div>
          <div className="filter-actions-rem">
            <button className="btn-generate" onClick={fetchAppointments}>GENERAR LISTADO</button>
            <button className="btn-export-orange" onClick={handleExport}><Download size={18} /></button>
          </div>
        </div>
        
        <div className="options-row">
          <label className="radio-label">
            <input 
              type="radio" 
              name="sendType" 
              value="web" 
              checked={sendType === 'web'} 
              onChange={() => setSendType('web')} 
            />
            Abrir en WhatsApp WEB
          </label>
          <label className="radio-label">
            <input 
              type="radio" 
              name="sendType" 
              value="pc" 
              checked={sendType === 'pc'} 
              onChange={() => setSendType('pc')} 
            />
            Abrir en App WhatsApp PC (más cómodo)
          </label>
        </div>
      </div>

      <div className="reminders-table-container card">
        <table className="reminders-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Staff</th>
              <th>Hora</th>
              <th>Paciente</th>
              <th>Nº Cel</th>
              <th className="text-center">Turno Pasado</th>
              <th className="text-center">Preparado</th>
              <th>Enviar</th>
              <th className="text-center">Expandir</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="text-center" style={{ padding: '2rem' }}>
                  Cargando turnos de la base de datos...
                </td>
              </tr>
            ) : appointments.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center" style={{ padding: '2rem' }}>
                  No se encontraron registros para mostrar.
                </td>
              </tr>
            ) : (
              appointments.map(rem => (
                <React.Fragment key={rem.id}>
                  <tr>
                    <td className="date-cell">{rem.appointment_date}</td>
                    <td className="staff-cell">{rem.professional_name || 'Sin Profesional'}</td>
                    <td className="time-col">{rem.appointment_time?.substring(0, 5)}</td>
                    <td className="patient-cell">{rem.patient_name}</td>
                    <td className="phone-cell">{rem.patient_phone || '---'}</td>
                    <td className="text-center">
                      {isPastAppointment(rem.appointment_date, rem.appointment_time) ? 'Sí' : 'No'}
                    </td>
                    <td className="text-center">
                      {rem.patient_phone ? <span className="prepared-check">✓</span> : '-'}
                    </td>
                    <td>
                      <button className="btn-send-ws" onClick={() => handleSend(rem)}>
                        ENVIAR <MessageSquare size={12} />
                      </button>
                    </td>
                    <td className="text-center">
                      <button className="btn-expand" onClick={() => toggleRow(rem.id)}>
                        <ChevronDown size={16} style={{ transform: expandedRows[rem.id] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                      </button>
                    </td>
                  </tr>
                  {expandedRows[rem.id] && (
                    <tr>
                      <td colSpan="9" style={{ padding: '0 1.5rem 1.5rem 1.5rem', background: 'rgba(255,255,255,0.01)' }}>
                        <div className="preview-container" style={{
                          padding: '1.25rem',
                          background: 'rgba(0, 0, 0, 0.25)',
                          border: '1px dashed var(--border-glass)',
                          borderRadius: '8px',
                          marginTop: '0.25rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--primary-cyan)' }}>VISTA PREVIA DEL MENSAJE</span>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Destinatario: {rem.patient_phone || 'Sin número'}</span>
                          </div>
                          <div className="whatsapp-bubble-preview" style={{
                            background: '#075e54',
                            padding: '0.85rem 1rem',
                            borderRadius: '8px',
                            color: '#fff',
                            fontFamily: 'Segoe UI, Helvetica Neue, Helvetica, Arial, sans-serif',
                            fontSize: '0.85rem',
                            lineHeight: '1.4',
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}>
                            {getWhatsAppMessageText(rem, localStorage.getItem('gdt_wa_auto_template'))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination placeholder matches screenshot layout */}
        <div className="reminders-table-pagination" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1.5rem', padding: '1rem', borderTop: '1px solid var(--border-glass)' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>-</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-pagination-arrow" disabled style={{ background: 'none', border: 'none', color: '#666', cursor: 'not-allowed' }}><ChevronLeft size={16} /></button>
            <button className="btn-pagination-arrow" disabled style={{ background: 'none', border: 'none', color: '#666', cursor: 'not-allowed' }}><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      <div className="floating-bot-container">
        <div className="bot-circle" onClick={() => addNotification('Asistente virtual de recordatorios activo.', 'info')}>
          <Bot size={24} />
        </div>
      </div>
    </div>
  );
};

export default WhatsAppManual;
